import Loading from "@lhd/components/Loading";
import { QUERY_NAME, ROUTES } from "@lhd/constants";
import { useFetchOneApp, useGetAuth, useGetPermissions } from "@lhd/hooks";
import { getUid } from "@lhd/utility";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useEffect, useMemo } from "react";
import {
  Navigate,
  Outlet,
  useLocation,
  useOutletContext,
  useParams,
} from "react-router-dom";
import ProtectedRoutes from "./ProtectRoute";

export const AppProtectedRoute: FC = () => {
  const { id: appId } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();
  const isTargetRoute = useMemo(() => {
    return /^\/apps\/[^/]+\/records\/(app-sheets|create)\/?$/.test(
      location.pathname,
    );
  }, [location.pathname]);
  const permission = useGetPermissions();
  const { isUserResigned } = useGetAuth();

  const {
    data: appData,
    isError: isErrorFetchApp,
    isLoading,
  } = useFetchOneApp(isTargetRoute ? appId : undefined);

  useEffect(() => {
    if (appId && isTargetRoute) {
      queryClient.invalidateQueries([QUERY_NAME.APP, appId, getUid()]);
    }
  }, [location.pathname, appId, queryClient, isTargetRoute]);

  const isAllowOnlyView = useMemo(() => {
    const cachedData = queryClient.getQueryData([
      QUERY_NAME.APP,
      appId,
      getUid(),
    ]);
    const currentAppData: any = appData || cachedData;

    if (isLoading) {
      return null;
    }

    if (isErrorFetchApp) {
      return false;
    } else if (currentAppData) {
      return (
        !(
          currentAppData?.current_detail_permissions?.specified_app_read &&
          !currentAppData?.current_detail_permissions?.specified_app_edit &&
          currentAppData?.current_detail_permissions?.records_permissions
            ?.general_record_read &&
          !currentAppData?.current_detail_permissions?.records_permissions
            ?.add_new_record &&
          !currentAppData?.current_detail_permissions?.records_permissions
            ?.general_record_edit
        ) || false
      );
    } else {
      return false;
    }
  }, [isErrorFetchApp, appData, appId, queryClient, isLoading]);

  if (!permission[ROUTES.APPS] || isUserResigned) {
    return <Navigate to={`/${ROUTES.NOT_FOUND_PAGE}`} replace />;
  }
  return <Outlet context={{ isAllowOnlyView }} />;
};

export const AppCreateProtected: FC = ({
  children,
}: {
  children?: React.ReactNode;
}) => {
  const { isAllowOnlyView } = useOutletContext<{
    isAllowOnlyView: boolean | null;
  }>();

  if (isAllowOnlyView === null) {
    return <Loading />;
  }

  return (
    <ProtectedRoutes
      condition={isAllowOnlyView}
      redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
    >
      {children}
    </ProtectedRoutes>
  );
};

export const AppDetailProtected: FC = ({
  children,
}: {
  children?: React.ReactNode;
}) => {
  const { isAllowOnlyView } = useOutletContext<{
    isAllowOnlyView: boolean | null;
  }>();

  if (isAllowOnlyView === null) {
    return <Loading />;
  }

  return (
    <ProtectedRoutes
      condition={isAllowOnlyView}
      redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
    >
      {children}
    </ProtectedRoutes>
  );
};
